import { useEffect } from 'react'
import { environment, FathomToken, AmplitudeAPIKey } from './config'
import * as Fathom from 'fathom-client'
import * as Sentry from '@sentry/browser'
import * as amplitude from '@amplitude/analytics-browser'
import { sanitisePath } from './utils/href'

const testMode = false //   process.env.NODE_ENV === 'test' || environment === 'development'

const log = false

const FathomEventIDs =
  environment === 'production'
    ? {
        'Action Completed': 'M6ETKWFO',
        'Action Created': 'EWOMJCJZ',
        'Action Opened': '0MKD8EWY',
        'Action Deleted': 'C44LZK2E',
        'Action Deletion Cancelled': 'LQHOMORO',
        'Action Uncompleted': '1G4VDCC5',
        'Action View Notes': 'ERPUIZFW',
        'Action View Reminders': 'PWWABELK',
        'Action View Schedule': 'DTXSCPU3',
        'List Created': '4YHW8XK2',
        'List View Colorpicker': 'FSKKIVNT',
        Login: '3I5BLAJV',
        Logout: 'IYCSEDEC',
        'Subtask Completed': 'FLM576XJ',
        'Subtask Created': 'FHP2WKX8',
        'Support Opened': 'F6QQMFR1',
      }
    : // staging
      {
        'Action Completed': 'VHMWE1DP',
        'Action Created': 'TF0ZJIXS',
        'Action Opened': '2ZDVO5AT',
        'Action Deleted': 'LHBRZ11L',
        'List Created': 'ICYTOMKR',
        'Action Deletion Cancelled': 'CCD0RKHD',
        'Action Uncompleted': 'VMKTJFDB',
        'Action View Notes': 'YC0IRHHS',
        'Action View Reminders': 'HDWVUVKS',
        'Action View Schedule': 'RFWY6NH2',
        'List View Colorpicker': 'ROHVJK3G',
        Login: '1UDRPWE6',
        Logout: 'BTVWD32N',
        'Subtask Completed': 'JNLHSW1M',
        'Subtask Created': '6OTPKYDS',
        'Support Opened': '1FJGHP17',
      }

const amplitudeNames = {
  Login: 'auth.login',
  Logout: 'auth.logout',
  'Action Completed': 'actions.action.complete',
  'Action Created': 'actions.action.create',
  'Action Opened': 'actions.action.open',
  'Action Deleted': 'actions.action.delete',
  'List Created': 'actions.list.create',
  // 'Action Deletion Cancelled': 'actions.action.deletion_cancelled',
  // 'Action Uncompleted': 'actions.action.uncomplete',
  'Action View Notes': 'actions.action.notes.view',
  'Action View Reminders': 'actions.action.reminders.view',
  'Action View Schedule': 'actions.action.schedule.view',
  'List View Colorpicker': 'actions.list.colorpicker.view',
  'Subtask Completed': 'actions.subtask.complete',
  'Subtask Created': 'actions.subtask.create',
  'Support Opened': 'actions.support.view',
}

const getAmplitudeEventName = (eventName) => {
  return amplitudeNames[eventName]
}

const sourcePlugin = {
  name: 'source-plugin',
  type: 'enrichment',
  setup: async () => {},
  execute: async (event) => {
    event.event_properties = {
      ...event.event_properties,
      app: 'actions_web',
    }
    return event
  },
}

export const initialise = () => {
  if (!FathomToken) return
  if (!AmplitudeAPIKey) return

  if (testMode) {
    if (log) console.log('Skipping analytics setup, in test mode')
    return
  }

  Fathom.load(FathomToken, { auto: false })
  amplitude.init(AmplitudeAPIKey, {
    autocapture: {
      sessions: true,
      // disabled:
      attribution: false,
      pageViews: false,
      formInteractions: false,
      fileDownloads: false,
      elementInteractions: false,
    },
    trackingOptions: {
      ipAddress: false,
    },
  })

  amplitude.add(sourcePlugin)
}

export const setAmplitudeUser = (amplitudeUserId) => {
  if (amplitudeUserId == null) {
    amplitude.reset()
    return
  }

  amplitude.setUserId(amplitudeUserId)
}

export const pageView = (location) => {
  // sanitise url - remove Action Ids
  const url = sanitisePath(location)

  if (testMode) {
    if (log) console.log(`Analytics test mode: Pageview - ${url}`)
    return
  }

  Fathom.trackPageview({ url })
}

const trackGoal = (event) => {
  if (!window.fathom || !window.fathom.beacon) return

  const code = event
  const cents = 0
  const hostname = window.location.protocol + '//' + window.location.hostname
  const siteId = FathomToken

  // sanitised pathname
  const pathname = sanitisePath(window.location.pathname)

  window.fathom.beacon({
    gcode: code,
    gval: cents,
    qs: JSON.stringify({}), // no query string
    p: pathname,
    h: hostname,
    r: document.referrer.indexOf(hostname) < 0 ? document.referrer : '',
    sid: siteId,
  })
}

export const trackEvent = (eventName) => {
  const eventId = FathomEventIDs[eventName]

  if (testMode) {
    if (log)
      console.log(`Analytics test mode: Event - ${eventName} (${eventId})`)
    return
  }

  if (eventId) {
    // use direct method so we have control over pathname sent to fathom
    trackGoal(eventId)
  }

  const amplitudeEventName = getAmplitudeEventName(eventName)
  if (amplitudeEventName) {
    amplitude.track(amplitudeEventName)
  }

  Sentry.addBreadcrumb({
    message: eventName,
    level: 'info',
  })
}

export const TrackEvent = ({ action: eventName }) => {
  useEffect(() => {
    trackEvent(eventName)
  }, [eventName])
  return null
}

export function* trackEventAsync(eventName) {
  trackEvent(eventName)
  yield
}

initialise()
